/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { client, urlFor } from '@/sanity/client';
import { ProductCard } from '../ui/ProductCard';
import { ChevronRight } from 'lucide-react';

export const TopSellers = () => {
	const navigate = useNavigate();
	const [activeSlide, setActiveSlide] = useState(0);
	const [products, setProducts] = useState<any[]>([]);

	useEffect(() => {
		const fetchFeaturedServices = async () => {
			const data =
				await client.fetch(`*[_type == "service" && isFeatured == true] | order(_createdAt asc)[0...3]{
				title,
				slug,
				description,
				image,
				price,
				isPerHour,
				isPOA,
				duration,
				format
			}`);
			setProducts(data);
		};

		fetchFeaturedServices();
	}, []);

	const nextSlide = () => {
		setActiveSlide((prev) => (prev === products.length - 1 ? 0 : prev + 1));
	};

	const handleLearnMore = (slug: string) => {
		navigate(`/services/${slug}`);
	};

	return (
		<section className='w-full py-20 px-8'>
			<div className='container mx-auto max-w-6xl '>
				<header className='max-w-full text-neutral-800 w-[427px] items-center mx-auto'>
					<h1 className='text-4xl font-bold leading-[56px] max-md:text-[30px] max-md:leading-[40px] text-center'>
						Our Top Sellers.
					</h1>
				</header>

				{/* Desktop view - Grid */}
				<div className='hidden md:grid md:grid-cols-3 gap-8 mt-16 max-md:mt-10 md:px-4'>
					{products.map((product, index) => (
						<ProductCard
							key={index}
							title={product.title}
							description={product.description}
							imageSrc={urlFor(product.image).url()}
							price={
								product.isPOA
									? 'Request Quote'
									: product.isPerHour
										? `R${product.price}/hr`
										: `R${product.price}`
							}
							duration={
								product.duration === 0
									? 'Tailored'
									: product.duration + ' ' + product.format
							}
							buttonText='Learn more'
							onClick={() => handleLearnMore(product.slug.current)}
						/>
					))}
				</div>

				{/* Mobile view - Carousel */}
				<div className='md:hidden w-full overflow-hidden relative mt-16 max-md:mt-10'>
					<div
						className='flex transition-all duration-500 ease-in-out'
						style={{ transform: `translateX(-${activeSlide * 100}%)` }}
					>
						{products.map((product, index) => (
							<div key={index} className='w-full flex-shrink-0 px-2.5'>
								<ProductCard
									title={product.title}
									description={product.description}
									imageSrc={urlFor(product.image).url()}
									price={
										product.isPOA
											? 'Request Quote'
											: product.isPerHour
												? `R${product.price}/hr`
												: `R${product.price}`
									}
									duration={
										product.duration === 0
											? 'Tailored'
											: product.duration + ' ' + product.format
									}
									buttonText='Learn more'
									onClick={() => handleLearnMore(product.slug.current)}
								/>
							</div>
						))}
					</div>

					{/* Carousel controls */}
					<div className='flex w-full items-center gap-5 justify-center mt-8'>
						<div className='flex items-center gap-2.5 my-auto p-2'>
							{products.map((_, index) => (
								<button
									key={index}
									onClick={() => setActiveSlide(index)}
									className='focus:outline-none'
									aria-label='Testimonial slide'
								>
									<div
										className={`${
											activeSlide === index
												? 'bg-[rgba(251,176,64,1)] h-4 w-4'
												: 'bg-[rgba(251,176,64,0.5)] h-3 w-3'
										} rounded-full transition-all duration-300`}
									/>
								</button>
							))}
						</div>
						<button
							onClick={nextSlide}
							className='flex items-center justify-center bg-[rgba(251,176,64,1)] rounded-full w-12 h-12 text-white transition-transform hover:scale-105 ml-4'
							aria-label='Testimonial slide'
						>
							<ChevronRight className='h-6 w-6' />
						</button>
					</div>
				</div>

				<div className='flex justify-center mt-12'>
					<button
						className='flex items-center gap-2 text-neutral-800 font-bold cursor-pointer'
						onClick={() => navigate('/services')}
						aria-label='Testimonial slide'
					>
						<span>View all</span>
						<div className='bg-[#FBB040] rounded-full w-8 h-8 flex items-center justify-center'>
							<svg
								xmlns='http://www.w3.org/2000/svg'
								width='16'
								height='16'
								viewBox='0 0 24 24'
								fill='none'
								stroke='currentColor'
								strokeWidth='3'
								strokeLinecap='round'
								strokeLinejoin='round'
							>
								<path d='M5 12h14M12 5l7 7-7 7' />
							</svg>
						</div>
					</button>
				</div>
			</div>
		</section>
	);
};
